import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import RegistrationForm from './components/RegistrationForm';
import IdentityVerification from './components/IdentityVerification';
import WhatsAppRegistration from './components/WhatsAppRegistration';

const Register = () => {
  const [currentStep, setCurrentStep] = useState('method'); // method, form, verification, success
  const [registrationMethod, setRegistrationMethod] = useState('');
  const [language, setLanguage] = useState('fr');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setLanguage(savedLanguage);
  }, []);

  const handleMethodSelect = (method) => {
    setRegistrationMethod(method);
    if (method === 'whatsapp') {
      setCurrentStep('whatsapp');
    } else {
      setCurrentStep('form');
    }
  };

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    
    try {
      // Simulate registration API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Save language preference
      localStorage.setItem('preferredLanguage', formData?.language);
      setLanguage(formData?.language);
      
      setRegistrationData(formData);
      setCurrentStep('verification');
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppRegister = async (whatsappData) => {
    setIsLoading(true);
    
    try {
      // Simulate WhatsApp registration
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setRegistrationData(whatsappData);
      // WhatsApp registration goes directly to success
      setCurrentStep('success');
    } catch (error) {
      console.error('WhatsApp registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationComplete = (verificationData) => {
    if (verificationData) {
      // User completed verification
      setRegistrationData(prev => ({ ...prev, verification: verificationData }));
    }
    setCurrentStep('success');
  };

  const handleCompleteRegistration = () => {
    // Simulate successful registration
    const mockToken = 'mock_auth_token_' + Date.now();
    const mockUser = {
      id: Date.now(),
      firstName: registrationData?.firstName || 'User',
      lastName: registrationData?.lastName || '',
      email: registrationData?.email || `user${Date.now()}@example.com`,
      phone: registrationData?.phone || registrationData?.phoneNumber || '6XXXXXXXX',
      language: language,
      isVerified: !!registrationData?.verification,
      registrationMethod: registrationMethod,
      createdAt: new Date()?.toISOString()
    };

    // Save auth data
    localStorage.setItem('authToken', mockToken);
    localStorage.setItem('userData', JSON.stringify(mockUser));

    // Navigate to dashboard
    navigate('/homepage-dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Progress Indicator */}
        {currentStep !== 'method' && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => {
                  if (currentStep === 'form' || currentStep === 'whatsapp') {
                    setCurrentStep('method');
                  } else if (currentStep === 'verification') {
                    setCurrentStep('form');
                  }
                }}
                className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name="ArrowLeft" size={16} />
                <span className="text-sm">
                  {language === 'fr' ? 'Retour' : 'Back'}
                </span>
              </button>
              
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${
                  ['form', 'verification', 'success']?.includes(currentStep) ? 'bg-primary' : 'bg-muted'
                }`} />
                <div className={`w-2 h-2 rounded-full ${
                  ['verification', 'success']?.includes(currentStep) ? 'bg-primary' : 'bg-muted'
                }`} />
                <div className={`w-2 h-2 rounded-full ${
                  currentStep === 'success' ? 'bg-primary' : 'bg-muted'
                }`} />
              </div>
            </div>
          </div>
        )}

        <div className="bg-card rounded-xl shadow-soft border border-border overflow-hidden">
          {/* Method Selection */}
          {currentStep === 'method' && (
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="UserPlus" size={32} className="text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {language === 'fr' ? 'Créer un compte' : 'Create Account'}
                </h1>
                <p className="text-muted-foreground">
                  {language === 'fr' ?'Rejoignez la communauté AutoCasse et commencez à acheter ou vendre' :'Join the AutoCasse community and start buying or selling'
                  }
                </p>
              </div>

              <div className="space-y-4">
                {/* Standard Registration */}
                <button
                  onClick={() => handleMethodSelect('standard')}
                  className="w-full flex items-center justify-between p-6 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-smooth"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Mail" size={24} className="text-primary" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-foreground">
                        {language === 'fr' ? 'Inscription complète' : 'Full Registration'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ?'Créez un compte avec email et mot de passe' :'Create account with email and password'
                        }
                      </p>
                    </div>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                </button>

                {/* WhatsApp Registration */}
                <button
                  onClick={() => handleMethodSelect('whatsapp')}
                  className="w-full flex items-center justify-between p-6 border border-success/20 bg-success/5 rounded-lg hover:border-success hover:bg-success/10 transition-smooth"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name="MessageCircle" size={24} className="text-success" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-foreground">
                        {language === 'fr' ? 'Inscription WhatsApp' : 'WhatsApp Registration'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ?'Inscription rapide via WhatsApp' :'Quick registration via WhatsApp'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-success text-success-foreground px-2 py-1 rounded-full">
                      {language === 'fr' ? 'Rapide' : 'Quick'}
                    </span>
                    <Icon name="ChevronRight" size={20} className="text-muted-foreground" />
                  </div>
                </button>
              </div>

              {/* Login Link */}
              <div className="text-center mt-8 pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Vous avez déjà un compte?' : 'Already have an account?'}
                  <Link 
                    to="/login" 
                    className="ml-2 text-primary hover:underline font-medium"
                  >
                    {language === 'fr' ? 'Se connecter' : 'Sign In'}
                  </Link>
                </p>
              </div>
            </div>
          )}

          {/* Standard Registration Form */}
          {currentStep === 'form' && (
            <div className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {language === 'fr' ? 'Informations du compte' : 'Account Information'}
                </h1>
                <p className="text-muted-foreground">
                  {language === 'fr' ?'Remplissez vos informations pour créer votre compte' :'Fill in your information to create your account'
                  }
                </p>
              </div>

              <RegistrationForm 
                onSubmit={handleFormSubmit}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* WhatsApp Registration */}
          {currentStep === 'whatsapp' && (
            <div className="p-8">
              <WhatsAppRegistration
                language={language}
                onWhatsAppRegister={handleWhatsAppRegister}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Identity Verification */}
          {currentStep === 'verification' && (
            <div className="p-8">
              <IdentityVerification
                language={language}
                onVerificationComplete={handleVerificationComplete}
              />
            </div>
          )}

          {/* Success */}
          {currentStep === 'success' && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={40} className="text-success" />
              </div>
              
              <h1 className="text-2xl font-bold text-foreground mb-4">
                {language === 'fr' ? 'Compte créé avec succès!' : 'Account Created Successfully!'}
              </h1>
              
              <p className="text-muted-foreground mb-6">
                {registrationMethod === 'whatsapp' ? (
                  language === 'fr' ?'Votre compte WhatsApp a été créé. Vous recevrez une confirmation dans WhatsApp.' :'Your WhatsApp account has been created. You will receive confirmation in WhatsApp.'
                ) : (
                  language === 'fr' ?'Votre compte a été créé. Vous pouvez maintenant commencer à utiliser AutoCasse.' :'Your account has been created. You can now start using AutoCasse.'
                )}
              </p>

              {registrationData?.verification && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2">
                    <Icon name="Shield" size={20} className="text-success" />
                    <span className="text-success font-medium">
                      {language === 'fr' ? 'Vérification en cours' : 'Verification Pending'}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {language === 'fr' ?'Votre identité sera vérifiée sous 24-48h' :'Your identity will be verified within 24-48h'
                    }
                  </p>
                </div>
              )}

              <Button
                variant="default"
                fullWidth
                onClick={handleCompleteRegistration}
                iconName="ArrowRight"
                iconPosition="right"
                iconSize={16}
              >
                {language === 'fr' ? 'Accéder au tableau de bord' : 'Go to Dashboard'}
              </Button>

              <p className="text-xs text-muted-foreground mt-4">
                {language === 'fr' ?'Vous serez redirigé vers votre tableau de bord' :'You will be redirected to your dashboard'
                }
              </p>
            </div>
          )}
        </div>

        {/* Language Selector */}
        <div className="text-center mt-6">
          <div className="inline-flex items-center space-x-2 bg-card border border-border rounded-lg p-2">
            <button
              onClick={() => setLanguage('fr')}
              className={`px-3 py-1 rounded text-sm transition-smooth ${
                language === 'fr' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Français
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded text-sm transition-smooth ${
                language === 'en' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              English
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;